import type {
    IRegEwcLookupItem,
    IWasteMovementDetail,
    WasteMovementConsignmentPayload,
    WasteMovementDirection,
    WasteMovementHazardousDetailPayload,
    WasteMovementPopsDetailPayload,
} from "@/customTypings/wasteMovement/WasteMovement";
import type {
    WasteMovementFormValues,
    WasteMovementHazardousComponentFormRow,
    WasteMovementHazardousPopsFormValues,
    WasteMovementPopComponentFormRow,
} from "../model/formValues";

/**
 * S1C hazardous, POPs and consignment capture for Waste Received. Vocabularies and rules mirror the backend
 * (`WasteMovementSourceOfComponents`, `WasteMovementNoConsignmentReasons`, `WasteMovementConsignmentCodeFormat`,
 * `WasteMovementComponentConcentration`, `WasteMovementValidationService.ValidateHazardousPopsConsignment`), which were
 * verified against the DEFRA Receipt API v1.0 production specification and DEFRA's validation package on 23 Sep 2026.
 */

export const SOURCE_NOT_PROVIDED = "NOT_PROVIDED";
export const SOURCE_PROVIDED_WITH_WASTE = "PROVIDED_WITH_WASTE";
export const SOURCE_GUIDANCE = "GUIDANCE";
export const SOURCE_OWN_TESTING = "OWN_TESTING";

/** DEFRA `sourceOfComponents` codes with DEFRA's meanings. */
export const SOURCE_OF_COMPONENTS_OPTIONS: readonly { code: string; label: string }[] = [
    { code: SOURCE_NOT_PROVIDED, label: "Not provided (no component details came with the waste)" },
    { code: SOURCE_PROVIDED_WITH_WASTE, label: "Provided with the waste" },
    { code: SOURCE_GUIDANCE, label: "Determined from guidance" },
    { code: SOURCE_OWN_TESTING, label: "Determined from our own testing" },
];

/** DEFRA `reasonForNoConsignmentCode` codes with the Receipt API's labels. */
export const NO_CONSIGNMENT_REASON_OPTIONS: readonly { code: string; label: string }[] = [
    { code: "NON_HAZ_WASTE_TRANSFER", label: "Non-Haz Waste Transfer" },
    { code: "NO_DOC_WITH_WASTE", label: "No documentation provided with Waste" },
    { code: "HWRC_RECEIPT", label: "Household Waste Recycling Centre Receipt" },
];

export const CONSIGNMENT_CODE_MAX_LENGTH = 64;
export const COMPONENT_NAME_MAX_LENGTH = 500;
export const CONCENTRATION_MAX_DECIMAL_PLACES = 9;

const EA_NRW_CONSIGNMENT = /^[A-Za-z0-9]{2,}\/[A-Za-z0-9]{5}[A-Za-z]?$/;
const SEPA_CONSIGNMENT = /^S[ABC][0-9]{7}$/;
const NIEA_CONSIGNMENT = /^D[ABC][0-9]{7}$/;

/** DEFRA's accepted formats: CJTILE/A0001 (England and Wales), SA1234567 (Scotland), DA1234567 (Northern Ireland). */
export function isValidConsignmentCode(code: string): boolean {
    const value = (code ?? "").trim();
    if (!value || value.length > CONSIGNMENT_CODE_MAX_LENGTH) return false;
    return EA_NRW_CONSIGNMENT.test(value) || SEPA_CONSIGNMENT.test(value) || NIEA_CONSIGNMENT.test(value);
}

const sourceRequiresComponents = (source: string) => source === SOURCE_GUIDANCE || source === SOURCE_OWN_TESTING;
const sourceForbidsComponents = (source: string) => source === SOURCE_NOT_PROVIDED;

/**
 * Concentration text (mg/kg) as typed. Blank is allowed (DEFRA: optional, and not the same as zero). Otherwise a plain
 * decimal greater than 0, at most 1,000,000 mg/kg and at most 9 decimal places. Checked on the text so no rounding hides
 * a problem. Returns the reason, or null when acceptable.
 */
export function concentrationProblem(text: string): string | null {
    const value = (text ?? "").trim();
    if (value === "") return null;
    const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
    if (!match) return "must be a number in mg/kg";
    const whole = match[1].replace(/^0+(?=\d)/, "");
    const fraction = match[2] ?? "";
    if (fraction.replace(/0+$/, "").length > CONCENTRATION_MAX_DECIMAL_PLACES)
        return `can have at most ${CONCENTRATION_MAX_DECIMAL_PLACES} decimal places`;
    const isZero = /^0*$/.test(whole) && /^0*$/.test(fraction);
    if (isZero) return "must be greater than 0 mg/kg";
    const fractionNonZero = /[1-9]/.test(fraction);
    if (whole.length > 7 || (whole.length === 7 && (whole > "1000000" || (whole === "1000000" && fractionNonZero))))
        return "cannot be more than 1,000,000 mg/kg";
    return null;
}

/** API number → the text shown in the form, without exponent notation or trailing zeros (5e-7 → "0.0000005"). */
export function formatConcentration(value: number | null | undefined): string {
    if (value == null || !Number.isFinite(value)) return "";
    return value.toFixed(CONCENTRATION_MAX_DECIMAL_PLACES).replace(/\.?0+$/, "");
}

function concentrationToNumber(text: string): number | null {
    const value = (text ?? "").trim();
    return value === "" ? null : Number(value);
}

function formatEwc(code: string): string {
    const digits = (code ?? "").replace(/\s/g, "");
    return /^\d{6}$/.test(digits) ? `${digits.slice(0, 2)} ${digits.slice(2, 4)} ${digits.slice(4, 6)}` : (code ?? "");
}

/** The EWC classification a save would be checked against: the stored snapshot unless the EWC selection changes. */
export type EffectiveEwcClassification = { isHazardous: boolean | null; code: string; changed: boolean };

export function effectiveEwcClassification(args: {
    isNew: boolean;
    detail: IWasteMovementDetail | null | undefined;
    regEwcCodeId: string;
    ewcs: readonly IRegEwcLookupItem[];
}): EffectiveEwcClassification {
    const detail = args.detail;
    if (!args.isNew && detail && (detail.regEwcCodeId ?? "") === args.regEwcCodeId) {
        // Unchanged EWC: the classification frozen when it was chosen (null = never recorded) — never today's list.
        return { isHazardous: detail.ewcIsHazardousSnapshot ?? null, code: formatEwc(detail.ewcCodeSnapshot), changed: false };
    }
    const selected = args.ewcs.find((e) => e.id === args.regEwcCodeId);
    return {
        isHazardous: typeof selected?.isHazardous === "boolean" ? selected.isHazardous : null,
        code: formatEwc(selected?.code ?? ""),
        changed: true,
    };
}

/** What the saved record already holds (S1C detail is only ever cleared deliberately). */
export type StoredHazardousPopsState = {
    containsHazardous: boolean | null;
    containsPops: boolean | null;
    hasHazardousDetail: boolean;
    hasPopsDetail: boolean;
    hasConsignment: boolean;
};

export function storedHazardousPopsState(detail: IWasteMovementDetail | null | undefined): StoredHazardousPopsState {
    return {
        containsHazardous: detail?.containsHazardous ?? null,
        containsPops: detail?.containsPops ?? null,
        hasHazardousDetail: !!detail?.hazardous,
        hasPopsDetail: !!detail?.pops,
        hasConsignment: !!detail?.consignment,
    };
}

export function emptyHazardousPopsFormValues(): WasteMovementHazardousPopsFormValues {
    return {
        hazardousSourceOfComponents: "",
        hazardousPropertyCodes: [],
        hazardousComponents: [],
        popsSourceOfComponents: "",
        popComponents: [],
        hazardousWasteConsignmentCode: "",
        reasonForNoConsignmentCode: "",
        confirmRemoveHazardousDetail: false,
        confirmRemovePopsDetail: false,
    };
}

export function hazardousPopsFormValuesFromDetail(
    detail: IWasteMovementDetail | null | undefined
): WasteMovementHazardousPopsFormValues {
    return {
        hazardousSourceOfComponents: detail?.hazardous?.sourceOfComponents ?? "",
        hazardousPropertyCodes: [...(detail?.hazardous?.hazardousPropertyCodes ?? [])],
        hazardousComponents: (detail?.hazardous?.components ?? []).map((c) => ({
            name: c.name,
            concentration: formatConcentration(c.concentrationMgPerKg),
        })),
        popsSourceOfComponents: detail?.pops?.sourceOfComponents ?? "",
        popComponents: (detail?.pops?.components ?? []).map((c) => ({
            code: c.code,
            concentration: formatConcentration(c.concentrationMgPerKg),
        })),
        hazardousWasteConsignmentCode: detail?.consignment?.hazardousWasteConsignmentCode ?? "",
        reasonForNoConsignmentCode: detail?.consignment?.reasonForNoConsignmentCode ?? "",
        confirmRemoveHazardousDetail: false,
        confirmRemovePopsDetail: false,
    };
}

export function hasHazardousFormDetail(
    values: Pick<WasteMovementFormValues, "hazardousSourceOfComponents" | "hazardousPropertyCodes" | "hazardousComponents">
): boolean {
    return values.hazardousSourceOfComponents !== "" || values.hazardousPropertyCodes.length > 0 || values.hazardousComponents.length > 0;
}

export function hasPopsFormDetail(values: Pick<WasteMovementFormValues, "popsSourceOfComponents" | "popComponents">): boolean {
    return values.popsSourceOfComponents !== "" || values.popComponents.length > 0;
}

export function hasConsignmentFormValue(
    values: Pick<WasteMovementFormValues, "hazardousWasteConsignmentCode" | "reasonForNoConsignmentCode">
): boolean {
    return values.hazardousWasteConsignmentCode.trim() !== "" || values.reasonForNoConsignmentCode.trim() !== "";
}

/** True when any S1C value is entered (counts as engaging receipt capture, like the backend's HasAnyDwtCapture). */
export function hasAnyHazardousPopsValue(values: WasteMovementFormValues): boolean {
    return hasHazardousFormDetail(values) || hasPopsFormDetail(values) || hasConsignmentFormValue(values);
}

export type HazardousPopsPayload = {
    hazardous?: WasteMovementHazardousDetailPayload;
    pops?: WasteMovementPopsDetailPayload;
    consignment?: WasteMovementConsignmentPayload;
    clearHazardous?: boolean;
    clearPops?: boolean;
    clearConsignment?: boolean;
};

/**
 * S1C part of the write payload, following the API's explicit semantics:
 * - hazardous / POP detail is sent as a whole while the answer is Yes and something is entered or stored; with nothing
 *   entered and nothing stored it is omitted (an S1B record stays as it was);
 * - a stored detail is removed only through the clear flag, sent after the operator answered No and confirmed removal;
 * - consignment values are sent when entered, and cleared when the stored values were blanked.
 * Create never sends clear flags (there is nothing stored to clear).
 */
export function buildHazardousPopsPayload(
    values: WasteMovementFormValues,
    movementDirection: WasteMovementDirection,
    args: { isNew: boolean; detail: IWasteMovementDetail | null | undefined }
): HazardousPopsPayload {
    if (movementDirection !== "Received") return {};
    const stored = storedHazardousPopsState(args.isNew ? null : args.detail);
    const payload: HazardousPopsPayload = {};

    if (values.containsHazardous === "true") {
        if (hasHazardousFormDetail(values) || stored.hasHazardousDetail) {
            payload.hazardous = {
                sourceOfComponents: values.hazardousSourceOfComponents || null,
                hazardousPropertyCodes: [...values.hazardousPropertyCodes],
                components: values.hazardousComponents.map((c) => ({
                    name: c.name.trim(),
                    concentrationMgPerKg: concentrationToNumber(c.concentration),
                })),
            };
        }
    } else if (stored.hasHazardousDetail) {
        payload.clearHazardous = true;
    }

    if (values.containsPops === "true") {
        if (hasPopsFormDetail(values) || stored.hasPopsDetail) {
            payload.pops = {
                sourceOfComponents: values.popsSourceOfComponents || null,
                components: values.popComponents.map((c) => ({
                    code: c.code.trim(),
                    concentrationMgPerKg: concentrationToNumber(c.concentration),
                })),
            };
        }
    } else if (stored.hasPopsDetail) {
        payload.clearPops = true;
    }

    if (hasConsignmentFormValue(values)) {
        payload.consignment = {
            hazardousWasteConsignmentCode: values.hazardousWasteConsignmentCode.trim() || null,
            reasonForNoConsignmentCode: values.reasonForNoConsignmentCode.trim() || null,
        };
    } else if (stored.hasConsignment) {
        payload.clearConsignment = true;
    }

    return payload;
}

export type ComponentRowErrors = { name?: string; code?: string; concentration?: string };

export type HazardousPopsErrors = {
    containsHazardous?: string;
    containsPops?: string;
    hazardousSourceOfComponents?: string;
    hazardousPropertyCodes?: string;
    /** A list-level message, or one entry per row. */
    hazardousComponents?: string | (ComponentRowErrors | undefined)[];
    popsSourceOfComponents?: string;
    popComponents?: string | (ComponentRowErrors | undefined)[];
    hazardousWasteConsignmentCode?: string;
    reasonForNoConsignmentCode?: string;
    confirmRemoveHazardousDetail?: string;
    confirmRemovePopsDetail?: string;
};

export type HazardousPopsValidationContext = {
    movementDirection: WasteMovementDirection;
    captureRequired: boolean;
    isNew: boolean;
    ewc: EffectiveEwcClassification;
    stored: StoredHazardousPopsState;
};

function answer(value: string): boolean | null {
    if (value === "true") return true;
    if (value === "false") return false;
    return null;
}

function rowErrors<T>(rows: T[], check: (row: T, index: number) => ComponentRowErrors | undefined) {
    const errors = rows.map(check);
    return errors.some((e) => e !== undefined) ? errors : undefined;
}

/**
 * Client mirror of the server's S1C rules so the operator sees them before saving. The server remains authoritative.
 * Unsaved values behind a No answer are simply not sent; only stored detail needs the explicit removal confirmation.
 */
export function validateHazardousPopsConsignment(
    values: WasteMovementFormValues,
    context: HazardousPopsValidationContext
): HazardousPopsErrors {
    const errors: HazardousPopsErrors = {};
    if (context.movementDirection !== "Received" || !context.captureRequired) return errors;
    const { ewc, stored, isNew } = context;
    const hazardous = answer(values.containsHazardous);
    const pops = answer(values.containsPops);

    // ------------------------------------------------------------ hazardous
    if (hazardous !== true) {
        if (!isNew && stored.hasHazardousDetail && !values.confirmRemoveHazardousDetail)
            errors.confirmRemoveHazardousDetail =
                "Confirm that the recorded hazardous details should be removed, or answer Yes.";
    } else {
        const entered = hasHazardousFormDetail(values);
        const transitional = !isNew && stored.containsHazardous === true && !stored.hasHazardousDetail && !entered;

        if (ewc.isHazardous === false) {
            if (values.hazardousPropertyCodes.length > 0 || values.hazardousComponents.length > 0)
                errors.hazardousPropertyCodes = `EWC ${ewc.code} is classified as non-hazardous, so hazardous property codes and components can't be recorded against it. Check the EWC code, or answer No and remove the hazardous details.`;
            else if (!(transitional && !ewc.changed))
                errors.containsHazardous = `EWC ${ewc.code} is classified as non-hazardous. Answer No to hazardous properties, or choose the correct hazardous EWC code.`;
        }

        const names = new Set<string>();
        const componentErrors = rowErrors(values.hazardousComponents, (row: WasteMovementHazardousComponentFormRow) => {
            const name = row.name.trim();
            const rowError: ComponentRowErrors = {};
            if (!name) rowError.name = "Enter the component name.";
            else if (name.length > COMPONENT_NAME_MAX_LENGTH)
                rowError.name = `Component names must be at most ${COMPONENT_NAME_MAX_LENGTH} characters.`;
            else if (names.has(name.toLowerCase())) rowError.name = `${name} is listed more than once.`;
            if (name) names.add(name.toLowerCase());
            const problem = concentrationProblem(row.concentration);
            if (problem) rowError.concentration = `Concentration ${problem}.`;
            return Object.keys(rowError).length ? rowError : undefined;
        });

        // Once the EWC rules out hazardous properties, asking for more of them would contradict that message.
        if (!transitional && !errors.hazardousPropertyCodes && !errors.containsHazardous) {
            if (!values.hazardousSourceOfComponents)
                errors.hazardousSourceOfComponents = "Select how the hazardous components were determined.";
            if (values.hazardousPropertyCodes.length === 0)
                errors.hazardousPropertyCodes = "Select at least one hazardous property (HP) code.";
            if (sourceForbidsComponents(values.hazardousSourceOfComponents) && values.hazardousComponents.length > 0)
                errors.hazardousComponents =
                    "Hazardous components can't be recorded when they were not provided. Remove them, or choose how they were determined.";
            else if (sourceRequiresComponents(values.hazardousSourceOfComponents) && values.hazardousComponents.length === 0)
                errors.hazardousComponents =
                    "Add at least one hazardous component when the components were determined from guidance or your own testing.";
        }
        if (!errors.hazardousComponents && componentErrors) errors.hazardousComponents = componentErrors;
    }

    // ------------------------------------------------------------ POPs
    if (pops !== true) {
        if (!isNew && stored.hasPopsDetail && !values.confirmRemovePopsDetail)
            errors.confirmRemovePopsDetail = "Confirm that the recorded POP details should be removed, or answer Yes.";
    } else {
        const entered = hasPopsFormDetail(values);
        const transitional = !isNew && stored.containsPops === true && !stored.hasPopsDetail && !entered;
        const codes = new Set<string>();
        const componentErrors = rowErrors(values.popComponents, (row: WasteMovementPopComponentFormRow) => {
            const rowError: ComponentRowErrors = {};
            if (!row.code) rowError.code = "Select the POP.";
            else if (codes.has(row.code)) rowError.code = `${row.code} is listed more than once.`;
            if (row.code) codes.add(row.code);
            const problem = concentrationProblem(row.concentration);
            if (problem) rowError.concentration = `Concentration ${problem}.`;
            return Object.keys(rowError).length ? rowError : undefined;
        });

        if (!transitional) {
            if (!values.popsSourceOfComponents)
                errors.popsSourceOfComponents = "Select how the POP components were determined.";
            if (sourceForbidsComponents(values.popsSourceOfComponents) && values.popComponents.length > 0)
                errors.popComponents =
                    "POP components can't be recorded when they were not provided. Remove them, or choose how they were determined.";
            else if (sourceRequiresComponents(values.popsSourceOfComponents) && values.popComponents.length === 0)
                errors.popComponents =
                    "Add at least one POP when the components were determined from guidance or your own testing.";
        }
        if (!errors.popComponents && componentErrors) errors.popComponents = componentErrors;
    }

    // ------------------------------------------------------------ consignment
    const code = values.hazardousWasteConsignmentCode.trim();
    const reason = values.reasonForNoConsignmentCode.trim();
    if (code && code.length > CONSIGNMENT_CODE_MAX_LENGTH)
        errors.hazardousWasteConsignmentCode = `The consignment code must be at most ${CONSIGNMENT_CODE_MAX_LENGTH} characters.`;
    else if (code && !isValidConsignmentCode(code))
        errors.hazardousWasteConsignmentCode =
            "Enter the consignment code in a recognised format, for example CJTILE/A0001 (England and Wales), SA1234567 (Scotland) or DA1234567 (Northern Ireland).";
    if (reason && !NO_CONSIGNMENT_REASON_OPTIONS.some((r) => r.code === reason))
        errors.reasonForNoConsignmentCode = "Reason for no consignment code is not recognised.";
    else if (code && reason)
        errors.reasonForNoConsignmentCode = "The reason only applies when there is no consignment code.";

    const consignmentRequired = ewc.isHazardous === true || (ewc.isHazardous === null && hazardous === true);
    if (consignmentRequired && !code && !reason) {
        const transitional = !isNew && !stored.hasConsignment && !ewc.changed && stored.containsHazardous === hazardous;
        if (!transitional)
            errors.hazardousWasteConsignmentCode =
                ewc.isHazardous === true
                    ? `EWC ${ewc.code} is hazardous: enter the consignment code, or the reason there isn't one.`
                    : "The waste contains hazardous properties and its EWC classification was not recorded: enter the consignment code, or the reason there isn't one.";
    }

    return errors;
}

/** Whether the consignment section is required (marker) and whether to show it at all. */
export function consignmentSectionState(
    values: WasteMovementFormValues,
    ewc: EffectiveEwcClassification,
    stored: StoredHazardousPopsState
): { visible: boolean; required: boolean } {
    const hazardous = values.containsHazardous === "true";
    const required = ewc.isHazardous === true || (ewc.isHazardous === null && hazardous);
    return { visible: required || hazardous || hasConsignmentFormValue(values) || stored.hasConsignment, required };
}
